import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import BodyClassName from 'react-body-classname';
import { get, includes } from 'lodash';
import { NewFourOhFourPage } from 'pages/Bundles';
import AppRoutes from 'routes';
import { useServerContext } from 'lib/ServerContext';

import applicationSelector from 'store/selectors/app';
import { Header } from 'components/brand/Header';
import { Footer } from 'components/brand/Footer';
import { ExperimentModal } from 'components/experiment/ExperimentModal';
import { PageMetadata } from 'pages/ApplicationWrapper/PageMetadata';
import { initializeApp, appReady } from 'store/app/slice';

import { getTollfreePhoneNumber } from 'store/pageMeta/slice';

import './applicationWrapper.scss';

export function ApplicationWrapper({
	tags,
	isLandingPage,
	phone,
	statusCode,
	noindex,
	sftest,
	actions,
	redirectURL,
	holiday,
	location,
}) {
	const serverContext = useServerContext();

	useEffect(() => {
		actions.initializeApp();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		actions.appReady();

		const stateScriptTag = document.getElementById(
			'preloaded-state-script',
		);

		if (stateScriptTag) {
			stateScriptTag.parentNode.removeChild(stateScriptTag);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	//  Pushes the app status code up into the server context
	if (serverContext) {
		serverContext.statusCode = statusCode; // eslint-disable-line no-param-reassign
	}
	if (serverContext && redirectURL) {
		serverContext.redirectURL = redirectURL; // eslint-disable-line no-param-reassign
	}
	return (
		<div className="application-wrapper">
			<BodyClassName className="reactfoot-body" />
			{!!tags && (
				<PageMetadata
					title={tags.title}
					og={tags.og}
					twitter={tags.twitter}
					meta={tags.meta}
					links={tags.links}
					structuredData={tags.structuredData}
					noindex={noindex}
					filteredPage={get(tags, 'filteredPage', false)}
				/>
			)}
			<Header
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
				hideMenu={isLandingPage}
				hideDealsLink={isLandingPage}
				formattedPhoneNumber={get(phone, 'nationalSparephone')}
				phoneNumber={get(phone, 'nationalSparephoneUnformatted')}
				holiday={holiday} // sf_12289_sfdc_holiday_messaging
			/>
			<main>
				{includes([500, 404, 410], statusCode) && (
					<NewFourOhFourPage statusCode={statusCode} />
				)}
				{!includes([500, 404, 410], statusCode) && <AppRoutes />}
			</main>
			{sftest && <ExperimentModal />}
			<Footer
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
				formattedPhoneNumber={get(phone, 'nationalSparephone')}
				phoneNumber={get(phone, 'nationalSparephoneUnformatted')}
				isLandingPage={isLandingPage}
				location={location}
			/>
		</div>
	);
}

ApplicationWrapper.propTypes = {
	noindex: PropTypes.bool,
	statusCode: PropTypes.number,
	redirectURL: PropTypes.string,
	tags: PropTypes.object,
	isLandingPage: PropTypes.bool,
	phone: PropTypes.object,
	sftest: PropTypes.bool,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}).isRequired,
	holiday: PropTypes.bool,
	location: PropTypes.object,
};

export function ApplicationWrapperWithLocation() {
	const location = useLocation();
	const dispatch = useDispatch();
	const selectorProps = useSelector((state) => applicationSelector(state, { location }));
	const actions = useMemo(
		() => bindActionCreators({ initializeApp, appReady, getTollfreePhoneNumber }, dispatch),
		[dispatch],
	);

	return (
		<ApplicationWrapper
			{...selectorProps}
			actions={actions}
			location={location}
		/>
	);
}

export const enhance = (Component) => Component;

export default ApplicationWrapperWithLocation;
